import React, { useState, useEffect } from 'react';
import { Store, CreditCard, Shield, Save, CheckCircle, FileText, AlertTriangle } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';
import CampoCompartido from '../../components/Field';
import ExportarDatos from '../../components/ExportarDatos';

const API_BASE = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/inventario`;

const EMPTY = {
    nombre_negocio: '', ruc: '', direccion: '', telefono: '',
    email: '', ciudad: '', logo_url: '', mensaje_ticket: '', moneda: 'Gs.',
    // Datos que exige la facturación electrónica. Hoy no se imprimen: se
    // piden ahora para que el día que se conecte el facturador el dato ya
    // esté cargado, y no haya que salir a buscarlo con el primer documento
    // rechazado.
    facturar_al_vender: true, modo_prueba: true,
    tipo_contribuyente: 'fisica', nombre_fantasia: '', razon_social: '',
    actividades_economicas: [],
    departamento: '', distrito: '',
    numero_casa: '', complemento_direccion: '',
    codigo_departamento: '', codigo_distrito: '', codigo_ciudad: '',
};

const TIPOS_CONTRIBUYENTE = [
    { valor: 'fisica', etiqueta: 'Persona física' },
    { valor: 'juridica', etiqueta: 'Persona jurídica' },
];

export default function TenantSettings() {
    const [activeTab, setActiveTab] = useState('general');
    const [form, setForm] = useState(EMPTY);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState(null);


    useEffect(() => {
        axios.get(`${API_BASE}/settings/`)
            .then(r => setForm({ ...EMPTY, ...r.data }))
            .catch(() => setError('No se pudo cargar la configuración'))
            .finally(() => setLoading(false));
    }, []);

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        try {
            const r = await axios.put(`${API_BASE}/settings/`, form);
            setForm({ ...EMPTY, ...r.data });
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        } catch (err) {
            // El servidor sabe por qué rechazó el dato —un RUC cuyo dígito no
            // cierra, por ejemplo—. Decirlo es más útil que un mensaje genérico
            // que deja al usuario adivinando qué campo revisar.
            const detalle = err.response?.data;
            const primero = detalle && typeof detalle === 'object'
                ? Object.values(detalle).flat()[0]
                : null;
            setError(primero || 'Error al guardar. Verificá los datos e intentá de nuevo.');
        } finally {
            setSaving(false);
        }
    };

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const tabs = [
        { id: 'general',      label: 'Datos del Local', icon: <Store size={18} /> },
        { id: 'facturacion',  label: 'Facturación',     icon: <FileText size={18} /> },
        { id: 'billing',      label: 'Suscripción',     icon: <CreditCard size={18} /> },
        { id: 'security',     label: 'Seguridad',       icon: <Shield size={18} /> },
    ];

    if (loading) return (
        <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-brand-500 border-t-transparent" />
        </div>
    );

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="titulo-pagina">Configuración del Sistema</h2>
                    <p className="text-sm text-slate-500 dark:text-gray-400">Personaliza la información de tu ferretería y gestiona tu cuenta</p>
                </div>
            </div>

            <div className="flex flex-col lg:flex-row gap-8">
                <div className="lg:w-64 flex flex-row lg:flex-col gap-2 overflow-x-auto pb-2">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-3 px-5 py-3.5 rounded-2xl font-bold text-sm transition-all whitespace-nowrap ${activeTab === tab.id ? 'bg-brand-500 text-negro-950 shadow-lg shadow-brand-500/20' : 'text-slate-500 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-white/5 border border-transparent'}`}
                        >
                            {tab.icon}
                            {tab.label}
                        </button>
                    ))}
                </div>

                <motion.div
                    key={activeTab}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="flex-1 bg-white dark:bg-slate-900/60 backdrop-blur-md border border-slate-200 dark:border-white/10 rounded-3xl p-6 sm:p-8 shadow-2xl"
                >
                    {activeTab === 'general' && (
                        <form onSubmit={handleSave} className="space-y-6">
                            <h3 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2 mb-6">
                                <Store className="text-brand-800" size={20} />
                                Información Comercial
                            </h3>

                            {error && (
                                <div className="p-4 bg-error-500/10 border border-error-500/20 rounded-2xl text-error-400 text-sm font-bold">
                                    {error}
                                </div>
                            )}

                            {saved && (
                                <div className="p-4 bg-exito-500/10 border border-exito-500/20 rounded-2xl text-exito-400 text-sm font-bold flex items-center gap-2">
                                    <CheckCircle size={16} />
                                    Cambios guardados correctamente
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <Field label="Nombre del Negocio" placeholder="Ej: Ferretería San José"
                                    value={form.nombre_negocio} onChange={v => set('nombre_negocio', v)} required />
                                <Field label="RUC Legal" placeholder="Ej: 80012345-0"
                                    value={form.ruc} onChange={v => set('ruc', v)} />
                                <Field label="Teléfono de Contacto" placeholder="0981 234 567"
                                    value={form.telefono} onChange={v => set('telefono', v)} />
                                <Field label="Correo Electrónico" placeholder="contacto@ferreteria.com" type="email"
                                    value={form.email} onChange={v => set('email', v)} />
                                <Field label="Moneda" placeholder="Gs."
                                    value={form.moneda} onChange={v => set('moneda', v)} />
                                <Field label="Número de casa" placeholder="1234"
                                    ayuda="Si la dirección no tiene numeración, poné 0."
                                    value={form.numero_casa} onChange={v => set('numero_casa', v)} />
                                <Field label="Complemento de dirección" placeholder="Piso, local o referencia"
                                    value={form.complemento_direccion}
                                    onChange={v => set('complemento_direccion', v)} />
                                {/* Departamento, distrito y ciudad van juntos y encadenados:
                                    el comprobante lleva el código además del nombre, y
                                    dejarlos escribir a mano solo habilita el error. */}
                                <div className="md:col-span-2">
                                    <SelectorGeografico form={form} set={set} />
                                </div>
                                <FieldSelect label="Tipo de Contribuyente" opciones={TIPOS_CONTRIBUYENTE}
                                    value={form.tipo_contribuyente} onChange={v => set('tipo_contribuyente', v)} />
                                <Field label="Nombre de Fantasía" placeholder="Si difiere de la razón social"
                                    value={form.nombre_fantasia} onChange={v => set('nombre_fantasia', v)} />
                                {/* La razón social no es el nombre del negocio. Si sos persona
                                    física el RUC está a tu nombre, y es ese el que va en el
                                    comprobante: si fuera el del negocio, el emisor no se
                                    correspondería con el RUC que lleva al lado. */}
                                <Field label="Razón Social"
                                    placeholder={form.tipo_contribuyente === 'fisica'
                                        ? 'Tu nombre y apellido, como figura en el RUC'
                                        : 'La razón social inscripta en el RUC'}
                                    ayuda="Es el nombre con el que está inscripto el RUC, y el que sale en los comprobantes. Puede ser distinto del nombre del negocio."
                                    value={form.razon_social} onChange={v => set('razon_social', v)} />
                                <div className="md:col-span-2">
                                    <ActividadesEconomicas
                                        actividades={form.actividades_economicas || []}
                                        onChange={v => set('actividades_economicas', v)} />
                                </div>
                                <div className="md:col-span-2 flex items-start gap-3 p-4 rounded-2xl bg-slate-100 dark:bg-white/5">
                                    <button
                                        type="button"
                                        role="switch"
                                        aria-checked={!!form.exigir_turno}
                                        onClick={() => set('exigir_turno', !form.exigir_turno)}
                                        className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors mt-0.5 ${form.exigir_turno ? 'bg-brand-500' : 'bg-slate-300 dark:bg-gray-600'}`}
                                    >
                                        <span className={`inline-block w-5 h-5 bg-white rounded-full shadow transition-transform ${form.exigir_turno ? 'translate-x-6' : 'translate-x-1'}`} />
                                    </button>
                                    <div>
                                        <p className="text-sm font-bold text-slate-700 dark:text-slate-200">
                                            Exigir turno de caja abierto para vender
                                        </p>
                                        <p className="ayuda-campo">
                                            Conviene activarlo cuando hay cajeros empleados manejando efectivo:
                                            el cierre es lo que vuelve atribuible un faltante. Si atendés vos el
                                            mostrador, dejalo apagado y vendé sin abrir turno.
                                        </p>
                                    </div>
                                </div>

                                <div className="md:col-span-2 flex items-start gap-3 p-4 rounded-2xl bg-slate-100 dark:bg-white/5">
                                    <button
                                        type="button"
                                        role="switch"
                                        aria-checked={!!form.facturar_al_vender}
                                        onClick={() => set('facturar_al_vender', !form.facturar_al_vender)}
                                        className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors mt-0.5 ${form.facturar_al_vender ? 'bg-brand-500' : 'bg-slate-300 dark:bg-gray-600'}`}
                                    >
                                        <span className={`inline-block w-5 h-5 bg-white rounded-full shadow transition-transform ${form.facturar_al_vender ? 'translate-x-6' : 'translate-x-1'}`} />
                                    </button>
                                    <div>
                                        <p className="text-sm font-bold text-slate-700 dark:text-slate-200">
                                            Emitir la factura al cobrar
                                        </p>
                                        <p className="ayuda-campo">
                                            Es como funciona una caja: se cobra y sale la factura. Si lo apagás,
                                            las ventas se facturan después desde Historial de Ventas —y una venta
                                            que nadie se acuerda de facturar no se recupera con un clic—.
                                            Si el timbrado falta o venció, la venta se registra igual y la
                                            factura queda pendiente.
                                        </p>
                                    </div>
                                </div>

                                {/* Se destaca en ámbar y no en gris como los otros dos: mientras
                                    está encendido, nada de lo que imprime el sistema sirve como
                                    comprobante, y eso no puede parecer un ajuste más de la lista. */}
                                <div className="md:col-span-2 flex items-start gap-3 p-4 rounded-2xl bg-alerta-50 dark:bg-alerta-950/40 ring-1 ring-alerta-200 dark:ring-alerta-800">
                                    <button
                                        type="button"
                                        role="switch"
                                        aria-checked={!!form.modo_prueba}
                                        onClick={() => set('modo_prueba', !form.modo_prueba)}
                                        className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors mt-0.5 ${form.modo_prueba ? 'bg-alerta-500' : 'bg-slate-300 dark:bg-gray-600'}`}
                                    >
                                        <span className={`inline-block w-5 h-5 bg-white rounded-full shadow transition-transform ${form.modo_prueba ? 'translate-x-6' : 'translate-x-1'}`} />
                                    </button>
                                    <div>
                                        <p className="text-sm font-bold text-slate-700 dark:text-slate-200">
                                            Marcar los comprobantes como prueba
                                        </p>
                                        <p className="ayuda-campo">
                                            Los comprobantes salen con la leyenda «documento de prueba, sin
                                            validez fiscal» y sin el timbrado. Dejalo encendido mientras sigas
                                            emitiendo tus comprobantes por tu medio habitual: si el sistema
                                            numera en paralelo bajo el mismo timbrado, terminás con dos
                                            comprobantes con el mismo número ante la DNIT.
                                        </p>
                                        <p className="ayuda-campo">
                                            Apagalo solo cuando el sistema sea el único que emite los
                                            comprobantes del negocio. Lo que ya se imprimió como prueba queda
                                            marcado para siempre, aunque después lo apagues.
                                        </p>
                                    </div>
                                </div>

                                <div className="md:col-span-2">
                                    <Field label="Dirección Física" placeholder="Av. Mariscal López 1234, Asunción"
                                        value={form.direccion} onChange={v => set('direccion', v)} />
                                </div>
                                <div className="md:col-span-2">
                                    <Field label="Mensaje en el Ticket de Venta" placeholder="¡Gracias por su compra! Vuelva pronto."
                                        value={form.mensaje_ticket} onChange={v => set('mensaje_ticket', v)} />
                                </div>
                                <div className="md:col-span-2">
                                    <Field label="URL del Logo (para tickets)" placeholder="https://..."
                                        value={form.logo_url} onChange={v => set('logo_url', v)} />
                                </div>

                                {/* Va aca y no escondido en un menu: la politica de
                                    privacidad y la carta que firma el propietario
                                    prometen que puede llevarse sus datos sin pedirlos. */}
                                <div className="md:col-span-2">
                                    <ExportarDatos />
                                </div>
                            </div>

                            <div className="pt-6 border-t border-slate-100 dark:border-white/5 flex justify-end">
                                <button
                                    type="submit"
                                    disabled={saving}
                                    className="flex items-center gap-2 bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-negro-950 px-8 py-3 rounded-2xl font-bold transition-all shadow-xl shadow-brand-500/20"
                                >
                                    <Save size={20} />
                                    {saving ? 'Guardando...' : 'Guardar Cambios'}
                                </button>
                            </div>
                        </form>
                    )}

                    {activeTab === 'facturacion' && (
                        <FacturacionTab faltantes={form.datos_emisor_faltantes || []} />
                    )}

                    {activeTab === 'billing' && <BillingTab />}

                    {activeTab === 'security' && (
                        <div className="space-y-6 text-center py-12">
                            <Shield size={64} className="mx-auto text-slate-300 dark:text-gray-700 opacity-20 mb-4" />
                            <p className="text-slate-500 dark:text-gray-400 max-w-xs mx-auto">La configuración de seguridad y roles de usuario estará disponible en la próxima actualización.</p>
                        </div>
                    )}
                </motion.div>
            </div>
        </div>
    );
}

const FACT_EMPTY = {
    timbrado: '', numero_establecimiento: '001', numero_punto_expedicion: '001',
    numero_desde: 1, numero_hasta: 9999999, fecha_inicio: '', fecha_vencimiento: '',
};

function FacturacionTab({ faltantes = [] }) {
    const [form, setForm] = useState(FACT_EMPTY);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState(null);
    const [config, setConfig] = useState(null);

    const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

    useEffect(() => {
        axios.get(`${API_BASE}/facturacion-config/`)
            .then(r => {
                if (r.data) {
                    setConfig(r.data);
                    setForm({
                        timbrado: r.data.timbrado || '',
                        numero_establecimiento: r.data.numero_establecimiento || '001',
                        numero_punto_expedicion: r.data.numero_punto_expedicion || '001',
                        numero_desde: r.data.numero_desde || 1,
                        numero_hasta: r.data.numero_hasta || 9999999,
                        fecha_inicio: r.data.fecha_inicio || '',
                        fecha_vencimiento: r.data.fecha_vencimiento || '',
                    });
                }
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        try {
            const r = await axios.put(`${API_BASE}/facturacion-config/`, form);
            setConfig(r.data);
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        } catch (err) {
            const d = err.response?.data;
            setError(Object.values(d || {}).flat().join(' ') || 'Error al guardar.');
        } finally {
            setSaving(false);
        }
    };

    const today = new Date().toISOString().split('T')[0];
    const vencido = config?.fecha_vencimiento && config.fecha_vencimiento < today;
    const proxVencer = config?.fecha_vencimiento && !vencido &&
        (new Date(config.fecha_vencimiento) - new Date()) < 30 * 24 * 60 * 60 * 1000;

    if (loading) return (
        <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-brand-500 border-t-transparent" />
        </div>
    );

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-3 mb-2">
                <FileText className="text-brand-800" size={20} />
                <div>
                    <h3 className="titulo-item">Configuración de Facturación</h3>
                    <p className="text-xs text-slate-500 dark:text-gray-400">Timbrado autoimpresor — solo contado</p>
                </div>
            </div>

            {/* Lo que falta para poder facturar electrónicamente.
                Se avisa acá, con tiempo, y no en el momento de cobrar: un
                negocio no debería enterarse de que le falta un dato fiscal con
                el cliente esperando en el mostrador. */}
            {faltantes.length > 0 && (
                <div className="flex items-start gap-3 p-4 rounded-2xl border bg-alerta-500/10 border-alerta-500/20 text-sm">
                    <AlertTriangle size={18} className="text-alerta-500 shrink-0 mt-0.5" />
                    <div>
                        <p className="font-bold text-slate-700 dark:text-slate-200">
                            Faltan datos para la facturación electrónica
                        </p>
                        <p className="text-xs text-slate-500 dark:text-gray-400 mt-1">
                            No afecta la facturación actual con timbrado autoimpresor. Completá en
                            <strong> Datos del Local</strong>: {faltantes.join(', ')}.
                        </p>
                    </div>
                </div>
            )}

            {/* Estado del timbrado */}
            {config && (
                <div className={`flex items-start gap-3 p-4 rounded-2xl border text-sm font-medium ${
                    vencido
                        ? 'bg-error-500/10 border-error-500/20 text-error-400'
                        : proxVencer
                        ? 'bg-alerta-500/10 border-alerta-500/20 text-alerta-400'
                        : 'bg-exito-500/10 border-exito-500/20 text-exito-400'
                }`}>
                    <AlertTriangle size={18} className="mt-0.5 shrink-0" />
                    <div>
                        {vencido
                            ? `Timbrado VENCIDO el ${new Date(config.fecha_vencimiento).toLocaleDateString('es-PY')}. Actualizá los datos.`
                            : proxVencer
                            ? `Timbrado próximo a vencer el ${new Date(config.fecha_vencimiento).toLocaleDateString('es-PY')}.`
                            : `Timbrado vigente hasta ${new Date(config.fecha_vencimiento).toLocaleDateString('es-PY')} — Próximo N°: ${config.numero_actual}`
                        }
                    </div>
                </div>
            )}

            {error && (
                <div className="p-4 bg-error-500/10 border border-error-500/20 rounded-2xl text-error-400 text-sm font-bold">{error}</div>
            )}
            {saved && (
                <div className="p-4 bg-exito-500/10 border border-exito-500/20 rounded-2xl text-exito-400 text-sm font-bold flex items-center gap-2">
                    <CheckCircle size={16} /> Configuración guardada correctamente
                </div>
            )}

            <form onSubmit={handleSave} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                        <Field label="Número de Timbrado *" placeholder="Ej: 12345678"
                            value={form.timbrado} onChange={v => setF('timbrado', v)} required />
                    </div>
                    <FieldNum label="N° Establecimiento" value={form.numero_establecimiento}
                        onChange={v => setF('numero_establecimiento', v.slice(0, 3).padStart(Math.min(v.length, 3), '0'))}
                        placeholder="001" maxLength={3} />
                    <FieldNum label="N° Punto de Expedición" value={form.numero_punto_expedicion}
                        onChange={v => setF('numero_punto_expedicion', v.slice(0, 3).padStart(Math.min(v.length, 3), '0'))}
                        placeholder="001" maxLength={3} />
                    <div>
                        <label htmlFor="config-fecha-inicio-vigencia" className="text-xs font-bold text-slate-500 dark:text-gray-500 uppercase tracking-wider px-1 block mb-2">Fecha Inicio Vigencia *</label>
                        <input id="config-fecha-inicio-vigencia" type="date" required value={form.fecha_inicio} onChange={e => setF('fecha_inicio', e.target.value)}
                            className="w-full bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-2xl px-5 py-3.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500/50 font-medium" />
                    </div>
                    <div>
                        <label htmlFor="config-fecha-vencimiento-timbrado" className="text-xs font-bold text-slate-500 dark:text-gray-500 uppercase tracking-wider px-1 block mb-2">Fecha Vencimiento Timbrado *</label>
                        <input id="config-fecha-vencimiento-timbrado" type="date" required value={form.fecha_vencimiento} onChange={e => setF('fecha_vencimiento', e.target.value)}
                            className="w-full bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-2xl px-5 py-3.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500/50 font-medium" />
                    </div>
                    <Field label="Número Desde" placeholder="1" type="number"
                        value={form.numero_desde} onChange={v => setF('numero_desde', parseInt(v) || 1)} />
                    <Field label="Número Hasta" placeholder="9999999" type="number"
                        value={form.numero_hasta} onChange={v => setF('numero_hasta', parseInt(v) || 9999999)} />
                </div>

                {config && (
                    <div className="bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-2xl p-4 text-sm">
                        <p className="text-xs font-bold text-slate-400 dark:text-gray-500 uppercase mb-2">Vista previa del número de factura</p>
                        <p className="font-mono text-lg font-bold text-slate-900 dark:text-white">
                            {form.numero_establecimiento || '001'}-{form.numero_punto_expedicion || '001'}-{String(config.numero_actual).padStart(7, '0')}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">Este será el número de la próxima factura emitida</p>
                    </div>
                )}

                <div className="pt-4 border-t border-slate-100 dark:border-white/5 flex justify-end">
                    <button type="submit" disabled={saving}
                        className="flex items-center gap-2 bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-negro-950 px-8 py-3 rounded-2xl font-bold transition-all shadow-xl shadow-brand-500/20">
                        <Save size={20} />
                        {saving ? 'Guardando...' : config ? 'Actualizar Timbrado' : 'Guardar Timbrado'}
                    </button>
                </div>
            </form>
        </div>
    );
}

function FieldNum({ label, value, onChange, placeholder, maxLength }) {
    return (
        <CampoCompartido
            label={label}
            className="space-y-2"
            labelClassName="text-xs font-bold text-slate-500 dark:text-gray-500 uppercase tracking-wider px-1 block"
        >
            <input
                type="text"
                placeholder={placeholder}
                value={value || ''}
                maxLength={maxLength}
                onChange={e => onChange(e.target.value)}
                className="w-full bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-2xl px-5 py-3.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500/50 font-mono font-bold text-lg text-center"
            />
        </CampoCompartido>
    );
}

function BillingTab() {
    const [info, setInfo] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/inventario/subscription/`)
            .then(r => setInfo(r.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const ESTADO_META = {
        activo:     { label: 'Activo',          cls: 'bg-exito-500 text-white' },
        trial:      { label: 'Prueba',           cls: 'bg-brand-500 text-negro-950' },
        vencido:    { label: 'Vencido',          cls: 'bg-error-500 text-white' },
        suspendido: { label: 'Suspendido',       cls: 'bg-error-500 text-white' },
    };

    const estado = info?.estado || 'suspendido';
    const meta = ESTADO_META[estado] || ESTADO_META.suspendido;

    if (loading) return (
        <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-brand-500 border-t-transparent" />
        </div>
    );

    return (
        <div className="space-y-6">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-6">Estado de la Suscripción</h3>
            <div className="p-6 bg-brand-500/10 border border-brand-500/20 rounded-3xl flex justify-between items-center gap-4">
                <div>
                    <p className="text-xs font-bold text-brand-800 uppercase tracking-widest mb-1">Plan Actual</p>
                    <h4 className="text-2xl font-bold text-slate-900 dark:text-white uppercase">
                        {info?.plan_name || 'Sin Plan'}
                    </h4>
                    {info?.paid_until && (
                        <p className="text-sm text-slate-500 dark:text-gray-400 mt-1">
                            {estado === 'vencido' ? 'Venció el' : 'Vence el'}:{' '}
                            {new Date(info.paid_until).toLocaleDateString('es-PY')}
                        </p>
                    )}
                    {estado === 'trial' && info?.dias_restantes != null && (
                        <p className="text-sm font-bold text-brand-800 dark:text-brand-400 mt-1">
                            {info.dias_restantes > 0
                                ? `${info.dias_restantes} días restantes de prueba`
                                : 'Período de prueba vencido'}
                        </p>
                    )}
                </div>
                <span className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase shrink-0 ${meta.cls}`}>
                    {meta.label}
                </span>
            </div>
            <p className="text-slate-400 dark:text-gray-500 text-sm">
                Para renovar o cambiar de plan, contactá con el administrador del sistema.
            </p>
        </div>
    );
}

function FieldSelect({ label, value, onChange, opciones }) {
    return (
        <CampoCompartido
            label={label}
            className="space-y-2"
            labelClassName="text-xs font-bold text-slate-500 dark:text-gray-500 uppercase tracking-wider px-1"
        >
            <select
                value={value || ''}
                onChange={e => onChange(e.target.value)}
                className="w-full bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-2xl px-5 py-3.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500/50"
            >
                {opciones.map(o => (
                    <option key={o.valor} value={o.valor}>{o.etiqueta}</option>
                ))}
            </select>
        </CampoCompartido>
    );
}

function Field({ label, value, onChange, placeholder, type = 'text', required = false, ayuda }) {
    return (
        <CampoCompartido
            label={label}
            hint={ayuda}
            className="space-y-2"
            labelClassName="text-xs font-bold text-slate-500 dark:text-gray-500 uppercase tracking-wider px-1"
        >
            <input
                type={type}
                required={required}
                placeholder={placeholder}
                value={value || ''}
                onChange={e => onChange(e.target.value)}
                className="w-full bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-2xl px-5 py-3.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500/50 transition-all placeholder:text-slate-400 dark:placeholder:text-gray-700 font-medium"
            />
        </CampoCompartido>
    );
}

/**
 * Las actividades económicas del emisor.
 *
 * Son varias y no una: en el documento electrónico el grupo de actividad
 * económica se repite, porque un contribuyente puede tener varias inscriptas
 * ante el fisco y el comprobante las declara todas. Antes eran dos campos
 * sueltos, de modo que el dato se cargaba sin error y quedaba incompleto.
 */
function ActividadesEconomicas({ actividades, onChange }) {
    const cambiar = (i, campo, valor) =>
        onChange(actividades.map((a, j) => (j === i ? { ...a, [campo]: valor } : a)));

    const agregar = () => onChange([...actividades, { codigo: '', descripcion: '' }]);
    const quitar = i => onChange(actividades.filter((_, j) => j !== i));

    return (
        <div className="p-4 rounded-2xl bg-slate-100 dark:bg-white/5">
            <p className="text-xs font-bold text-slate-500 dark:text-gray-500 uppercase tracking-wider px-1">
                Actividades económicas
            </p>
            <p className="ayuda-campo">
                Cargá todas las que tengas inscriptas: el comprobante las declara todas.
                Los códigos tienen que ser los que figuran en tu constancia de RUC.
            </p>

            {actividades.length === 0 && (
                <p className="mt-3 text-sm font-semibold text-alerta-700 dark:text-alerta-400">
                    Todavía no cargaste ninguna. Hace falta al menos una para facturar.
                </p>
            )}

            {actividades.map((a, i) => (
                <div key={i} className="mt-3 flex items-start gap-2">
                    <input
                        aria-label={`Código de la actividad ${i + 1}`}
                        placeholder="47523"
                        value={a.codigo || ''}
                        onChange={e => cambiar(i, 'codigo', e.target.value)}
                        className="w-32 shrink-0 bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-3 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500/50 font-medium"
                    />
                    <input
                        aria-label={`Descripción de la actividad ${i + 1}`}
                        placeholder="Venta al por menor de artículos de ferretería"
                        value={a.descripcion || ''}
                        onChange={e => cambiar(i, 'descripcion', e.target.value)}
                        className="flex-1 bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-3 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500/50 font-medium"
                    />
                    <button
                        type="button"
                        aria-label={`Quitar la actividad ${i + 1}`}
                        onClick={() => quitar(i)}
                        className="shrink-0 px-3 py-2.5 rounded-xl text-error-700 dark:text-error-400 hover:bg-error-50 dark:hover:bg-error-950/40 font-bold"
                    >
                        Quitar
                    </button>
                </div>
            ))}

            <button
                type="button"
                onClick={agregar}
                className="mt-3 px-4 py-2 rounded-xl bg-slate-200 dark:bg-white/10 text-slate-700 dark:text-slate-200 font-bold text-sm"
            >
                Agregar actividad
            </button>
        </div>
    );
}

/**
 * Departamento, distrito y ciudad, encadenados.
 *
 * El comprobante electrónico no lleva el nombre de la ciudad sino su código,
 * junto con la descripción. Dejar escribir el código a mano solo habilita el
 * error: un código equivocado no se descubre hasta que el documento vuelve
 * rechazado, y para entonces ya se emitió.
 *
 * Van encadenados porque son casi siete mil ciudades. En una lista plana el
 * ferretero no encuentra la suya; eligiendo primero el departamento y después
 * el distrito, la última lista tiene unas pocas.
 */
function SelectorGeografico({ form, set }) {
    const [departamentos, setDepartamentos] = useState([]);
    const [distritos, setDistritos] = useState([]);
    const [ciudades, setCiudades] = useState([]);

    const pedir = async params => {
        const { data } = await axios.get(`${API_BASE}/geografia/`, { params });
        return data;
    };

    useEffect(() => {
        pedir({}).then(d => setDepartamentos(d.departamentos || [])).catch(() => {});
    }, []);

    useEffect(() => {
        if (!form.codigo_departamento) { setDistritos([]); return; }
        pedir({ departamento: form.codigo_departamento })
            .then(d => setDistritos(d.distritos || [])).catch(() => setDistritos([]));
    }, [form.codigo_departamento]);

    useEffect(() => {
        if (!form.codigo_departamento || !form.codigo_distrito) { setCiudades([]); return; }
        pedir({ departamento: form.codigo_departamento, distrito: form.codigo_distrito })
            .then(d => setCiudades(d.ciudades || [])).catch(() => setCiudades([]));
    }, [form.codigo_departamento, form.codigo_distrito]);

    // Cada selección guarda el código y el nombre: el comprobante lleva los
    // dos, y el proveedor que arma el documento no puede deducir uno del otro.
    const elegirDepartamento = codigo => {
        const d = departamentos.find(x => x.codigo === codigo);
        set('codigo_departamento', codigo);
        set('departamento', d ? d.nombre : '');
        set('codigo_distrito', ''); set('distrito', '');
        set('codigo_ciudad', ''); set('ciudad', '');
    };
    const elegirDistrito = codigo => {
        const d = distritos.find(x => x.codigo === codigo);
        set('codigo_distrito', codigo);
        set('distrito', d ? d.nombre : '');
        set('codigo_ciudad', ''); set('ciudad', '');
    };
    const elegirCiudad = codigo => {
        const c = ciudades.find(x => x.codigo === codigo);
        set('codigo_ciudad', codigo);
        set('ciudad', c ? c.nombre : '');
    };

    const clase = 'w-full bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-2xl px-4 py-3 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500/50 font-medium disabled:opacity-50';

    return (
        <div className="p-4 rounded-2xl bg-slate-100 dark:bg-white/5">
            <p className="text-xs font-bold text-slate-500 dark:text-gray-500 uppercase tracking-wider px-1">
                Ubicación del local
            </p>
            <p className="ayuda-campo">
                El comprobante lleva el código de cada uno además del nombre, así que se
                eligen de la lista oficial y no se escriben.
            </p>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <select aria-label="Departamento" className={clase}
                    value={form.codigo_departamento || ''}
                    onChange={e => elegirDepartamento(e.target.value)}>
                    <option value="">Departamento…</option>
                    {departamentos.map(d => (
                        <option key={d.codigo} value={d.codigo}>{d.nombre}</option>
                    ))}
                </select>
                <select aria-label="Distrito" className={clase}
                    disabled={!distritos.length}
                    value={form.codigo_distrito || ''}
                    onChange={e => elegirDistrito(e.target.value)}>
                    <option value="">Distrito…</option>
                    {distritos.map(d => (
                        <option key={d.codigo} value={d.codigo}>{d.nombre}</option>
                    ))}
                </select>
                <select aria-label="Ciudad" className={clase}
                    disabled={!ciudades.length}
                    value={form.codigo_ciudad || ''}
                    onChange={e => elegirCiudad(e.target.value)}>
                    <option value="">Ciudad…</option>
                    {ciudades.map(c => (
                        <option key={c.codigo} value={c.codigo}>{c.nombre}</option>
                    ))}
                </select>
            </div>
        </div>
    );
}
